import api from '@/api'
import axiosApi from '@/config/api'
import store from 'store'
import cookies from 'js-cookie'
import { parseJwtPayload } from '@/utils/parseJwt'

export default {
  state: {
    token: null,
    profile: {},
    preferences: {},
    access: {
      user: [],
      viewer: [],
      stagehand: [],
      stations: [],
      workspaceGroups: [],
      admin: false,
      dashboard: false
    },
    isTokenizedAccess: false,
    subscriptions: []
  },
  mutations: {
    SET_USER_DATA(state, userData) {
      state.token = userData.token
      state.profile = userData.profile
      state.access = userData.access

      // don't store tokens in localstore
      delete userData.token

      store.set('ews_user', userData)
    },
    SET_USER_PREFERENCES(state, preferences) {
      state.preferences = preferences
    },
    SET_USER_SUBSCRIPTIONS(state, subscriptions) {
      state.subscriptions = subscriptions
    },
    CLEAR_USER_DATA() {
      store.remove('ews_user')
      store.remove('ews_current_station')
      store.remove('ews_station_settings')
      store.remove('ews_container')
      const baseDomain = location.hostname.split('.').slice(-2).join('.')
      cookies.remove('EWS_AUTH_KEY', { domain: `.${baseDomain}` })
      location.reload()
    },
    SET_IS_TOKENIZED_ACCESS(state) {
      state.isTokenizedAccess = true
    }
  },
  actions: {
    /**
     * Initialize the current authenticated user's profile and station access.
     *
     * Reads the JWT cookie and persisted session, then takes one of three paths:
     *   - Tokenized user: exchanges the short-lived token for a fresh JWT.
     *   - Returning session: cached access is still present — commits as-is.
     *   - Fresh login: fetches station permissions and DB-authoritative profile
     *     fields (name, email, empid, user_id) from GET /v2/newsdesk/access.
     *
     * @param {boolean} loadExtras - Also load stations, groups, and notification
     *   preferences after the user is resolved. Pass false during token refresh
     *   to avoid redundant reloads.
     */
    async loadCurrentUser({ commit, dispatch }, loadExtras = true) {
      // ── 1. Bootstrap from persisted state + JWT ──────────────────────────────
      // JWT is parsed only for the initial empid/isTokenizedUser check.
      // All profile fields are overwritten with DB-authoritative values below.
      let data = store.get('ews_user', {})
      data.token = cookies.get('EWS_AUTH_KEY') ?? ''
      const { profile } = parseJwtPayload(data.token) ?? { profile: {} }
      const storedProfile = data.profile ?? {}
      data.profile = {
        ...profile,
        // DB-authoritative values from the access API take precedence; these
        // are preserved from the previous session as a fast-path fallback.
        first_name: profile.first_name || storedProfile.first_name || '',
        last_name: profile.last_name || storedProfile.last_name || ''
      }

      if (data.access && data.profile.empid) {
        // ── 2a. Returning session: access already loaded ──────────────────────
        // Cached access data is present; no need to re-fetch from the API.

        if (data.profile?.isTokenizedUser && data.profile?.tokenizedAccessToken) {
          // ── 2a-i. Tokenized access: refresh the token on every load ─────────
          // Tokenized JWTs are short-lived; always exchange for a fresh one.
          const tokenValue = data.profile.tokenizedAccessToken
          let isNewData = true
          await axiosApi
            .post(`/v2/auth/tokenized-access/generate-access/${tokenValue}`)
            .then((response) => {
              if (response.data.jwt) {
                const authData = {
                  ews_auth_key: response.data.jwt || '',
                  ews_auth_user: JSON.stringify({
                    profile: response.data.profile || {},
                    access: response.data.access || {}
                  }),
                  ews_auth_station: JSON.stringify(response.data.station || {}),
                  ews_auth_container: JSON.stringify(response.data.container || {})
                }
                dispatch('tokenizedAccessAuth', authData)
                commit('SET_USER_DATA', {
                  token: response.data.jwt || '',
                  profile: response.data.profile || {},
                  access: response.data.access || {}
                })
              } else {
                isNewData = false
              }
            })
            .catch(() => {
              isNewData = false
            })

          if (!isNewData) {
            commit('SET_USER_DATA', data)
          }
        } else {
          // ── 2a-ii. Standard returning session ────────────────────────────────
          // Okta org AS tokens carry no user_id custom claim. If it's absent,
          // make one API call to resolve it — after which it's stored in
          // localStorage and this branch won't need the API again.
          if (!data.profile.user_id || !data.profile.first_name || !data.profile.last_name) {
            try {
              const _access = await api.newsdesk.getStationAccessList()
              if (_access.data.user_id) data.profile.user_id = _access.data.user_id
              if (_access.data.first_name) data.profile.first_name = _access.data.first_name
              if (_access.data.last_name) data.profile.last_name = _access.data.last_name
            } catch (error) {
              console.warn('Could not fetch user profile from access API', error)
            }
          }
          commit('SET_USER_DATA', data)
        }
      } else {
        // ── 2b. Fresh login or expired access: fetch access from API ─────────
        // No cached access data — call /access to get station permissions and
        // DB-authoritative profile fields (name, email, empid, user_id).
        data.access = { user: [], viewer: [], stagehand: [], workspaceGroups: [], admin: false, dashboard: false }

        try {
          const _access = await api.newsdesk.getStationAccessList()
          data.access.admin = _access.data.admin ?? false
          data.access.dashboard = _access.data.dashboard ?? false
          data.access.user = _access.data.user
          data.access.viewer = _access.data.viewer
          data.access.stagehand = _access.data.stagehand
          data.access.workspaceGroups = _access.data.workspace_groups ?? []
          // Overwrite JWT-parsed profile fields with DB-authoritative values.
          if (_access.data.first_name) data.profile.first_name = _access.data.first_name
          if (_access.data.last_name) data.profile.last_name = _access.data.last_name
          if (_access.data.email) data.profile.email_address = _access.data.email
          if (_access.data.empid) data.profile.empid = _access.data.empid
          if (_access.data.user_id) data.profile.user_id = _access.data.user_id
        } catch (error) {
          console.error('Could not get station access list', error)
          return
        }

        data.access.stations = [...new Set([...data.access.user, ...data.access.viewer, ...data.access.stagehand])]
        commit('SET_USER_DATA', data)
      }

      if (loadExtras) {
        await dispatch('loadStations')
        await dispatch('loadGroups')
        try {
          await dispatch('loadNotificationEnabledStations')
        } catch (error) {
          console.error('Error loading notification enabled stations', error)
        }
      }

      return true
    },

    validateToken({ commit, dispatch }, payload) {
      return api.newsdesk.validateToken()
    },

    async refreshToken({ commit, dispatch }) {
      let response = await api.newsdesk.refreshToken()
      // write token to cookie
      // domain should be our base domain
      const baseDomain = location.hostname.split('.').slice(-2).join('.')
      cookies.set('EWS_AUTH_KEY', response.data.jwt, { domain: `.${baseDomain}` })
      // reload token
      await dispatch('loadCurrentUser', false)
    },

    async loadSubscriptions({ commit }, skipCache) {
      let resp = await api.newsdesk.getUserSubscriptions(!!skipCache)
      commit('SET_USER_SUBSCRIPTIONS', resp.data)
    },

    async reloadStationAccess({ commit, dispatch }) {
      let data = store.get('ews_user', {})
      data.access = null
      commit('SET_USER_DATA', data)
      await dispatch('loadCurrentUser')
    },
    async clearStationAccess({ commit, dispatch }) {
      let data = store.get('ews_user', {})
      data.access = null
      commit('SET_USER_DATA', data)
    },
    async loadUserPreferences({ commit, dispatch }) {
      let data = store.get('ews_user', {})
      let resp = await api.preferences.getUserPreferences(data.profile.empid)
      commit('SET_USER_PREFERENCES', resp.data)

      // Notify view presets system that user preferences are loaded
      dispatch('notifyUserPreferencesLoaded', {}, { root: true })
    },
    async saveUserPreferences({ dispatch }, payload) {
      let data = store.get('ews_user', {})

      // workaround for bug https://gitlab.scrippsnet.app/biztech/newsdesk/-/issues/772
      if (Array.isArray(payload.data) && payload.data.length == 1) payload.data.push({})
      let resp = await api.preferences.updateUserPreferences(data.profile.empid, payload.key, payload.data)
      await dispatch('loadUserPreferences')
    },
    async createUserPreferences({ dispatch }, payload) {
      let data = store.get('ews_user', {})
      let resp = await api.preferences.createUserPreferences(data.profile.empid, payload)
      await dispatch('loadUserPreferences')
    },
    async deleteUserPreferences({ dispatch }, key) {
      let data = store.get('ews_user', {})
      let resp = await api.preferences.deleteUserPreferences(data.profile.empid, key)
      await dispatch('loadUserPreferences')
    },
    async logout({ commit, dispatch }) {
      commit('CLEAR_USER_DATA')

      await dispatch('oneSignalLogout')
    },

    tokenizedAccessAuth({ commit }, payload) {
      const EWS_AUTH_KEY = payload?.ews_auth_key || ''
      const EWS_AUTH_USER = payload?.ews_auth_user || '{}'
      const EWS_AUTH_STATION = payload?.ews_auth_station || '{}'
      const EWS_AUTH_CONTAINER = payload?.ews_auth_container || '{}'
      const user = JSON.parse(EWS_AUTH_USER)
      const station = JSON.parse(EWS_AUTH_STATION)
      const container = JSON.parse(EWS_AUTH_CONTAINER)
      cookies.set('EWS_AUTH_KEY', EWS_AUTH_KEY)
      store.set('ews_user', user)
      store.set('ews_current_station', station)
      commit('SET_IS_TOKENIZED_ACCESS')
    },

    onAuthKeyRemoved({ state }, callback) {
      // it is kind of logout user -- Navbar Profile logout remove EWS_AUTH_KEY cookie
      if (typeof callback != 'function') callback = () => {}
      setInterval(async () => {
        const cookieToken = cookies.get('EWS_AUTH_KEY') || null
        const storeToken = state.token || null
        if (cookieToken !== storeToken && !cookieToken) {
          await callback()
        }
      }, 200)
    }
  },
  getters: {
    profile(state) {
      return state.profile
    },
    access(state) {
      return state.access
    },
    preferences(state) {
      return state.preferences
    },
    subscriptions(state) {
      return state.subscriptions
    },
    loggedIn(state) {
      return !!state.token
    },
    token(state) {
      return state.token
    },
    isAdminUser(state) {
      // return false
      return state.access?.admin
    },
    isStagehandUser(state, getters, rootState) {
      // return false
      return state.access?.stagehand?.includes(rootState.station.current?.station) || getters.isAdminUser
    },
    isPccUser(state, getters, rootState) {
      return state.access?.pcc?.includes(rootState.station.current?.station) || getters.isAdminUser
    },
    isNewsDeskUser(state, getters, rootState) {
      return state.access?.user?.includes(rootState.station.current?.station) || getters.isStagehandUser
    },
    isNewsDeskViewer(state, getters, rootState) {
      return state.access?.viewer?.includes(rootState.station.current?.station) || getters.isNewsDeskUser
    },
    isDashboardUser(state, getters) {
      // return true
      return state.access?.dashboard || getters.isAdminUser
    },
    isTokenizedAccess(state) {
      return state.isTokenizedAccess
    },
    userId(state) {
      return state.profile?.user_id ?? null
    }
  }
}
